import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import {
  Add,
  Delete,
  Edit,
  ExpandLess,
  ExpandMore,
  Refresh,
} from '@mui/icons-material';

import { EmailTemplateTypes } from '../../../utils/enums/userTypes';

import { deleteCampaign, getCampaigns } from '../../../api/campaign';

import CampaignEditorDialog from './CampaignEditorDialog';
import FormMailCampaignDetails from './FormMailCampaignDetails';

const emptyCampaign = {
  campaignTitle: '',
  campaignValidTo: '',
  mailTemplate: '',
  mailContent: '',
  active: 1,
  created_at: new Date().toISOString(),
};

const getTemplateName = (key) => {
  return (
    EmailTemplateTypes.find((item) => item.key === key)?.name ?? key ?? '-'
  );
};

const formatDate = (value) => {
  if (!value) return '-';

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '-';
  }

  return date.toLocaleDateString('hu-HU');
};

const FormMailCampaign = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editedCampaign, setEditedCampaign] = useState(null);
  const [loading, setLoading] = useState(false);

  async function loadCampaigns() {
    setLoading(true);

    try {
      const result = await getCampaigns({ onlyNotSent: 1 });
      setCampaigns(Array.isArray(result) ? result : []);
    } catch (error) {
      console.error('Kampányok betöltési hiba:', error);
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadCampaigns();
  }, []);

  const filteredCampaigns = useMemo(() => {
    const needle = search.toLowerCase();

    return campaigns.filter((campaign) => {
      const value = `
        ${campaign.campaignTitle ?? ''}
        ${campaign.mailTemplate ?? ''}
        ${campaign.mailContent ?? ''}
      `.toLowerCase();

      return value.includes(needle);
    });
  }, [campaigns, search]);

  const handleCreate = () => {
    setEditedCampaign(emptyCampaign);
    setEditorOpen(true);
  };

  const handleEdit = (campaign) => {
    setEditedCampaign(campaign);
    setEditorOpen(true);
  };

  const handleDelete = async (campaign) => {
    const confirmed = window.confirm(
      `Biztosan törlöd ezt a kampányt?\n\n${campaign.campaignTitle ?? ''}`,
    );

    if (!confirmed) return;

    await deleteCampaign(campaign.id);
    await loadCampaigns();
  };

  const handleToggleExpanded = (campaignId) => {
    setExpandedId((current) => (current === campaignId ? null : campaignId));
  };

  return (
    <Box sx={{ p: 2 }}>
      <Card sx={{ borderRadius: 3 }}>
        <CardContent>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            justifyContent='space-between'
            alignItems={{ xs: 'stretch', md: 'center' }}
            sx={{ mb: 2 }}
          >
            <Box>
              <Typography variant='h6' fontWeight={700}>
                e-Mail kampányok
              </Typography>

              <Typography variant='body2' color='text.secondary'>
                Kampányok kezelése és címzettek hozzárendelése
              </Typography>
            </Box>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <TextField
                size='small'
                label='Keresés'
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />

              <Button
                variant='outlined'
                startIcon={<Refresh />}
                onClick={loadCampaigns}
                disabled={loading}
              >
                Frissítés
              </Button>

              <Button
                variant='contained'
                startIcon={<Add />}
                onClick={handleCreate}
              >
                Új kampány
              </Button>
            </Stack>
          </Stack>

          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell width={48} />
                <TableCell>Kampány</TableCell>
                <TableCell>Érvényesség vége</TableCell>
                <TableCell>e-Mail template</TableCell>
                <TableCell>Aktív</TableCell>
                <TableCell align='right'>Műveletek</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredCampaigns.map((campaign) => {
                const isExpanded = expandedId === campaign.id;

                return (
                  <>
                    <TableRow key={campaign.id} hover>
                      <TableCell>
                        <IconButton
                          size='small'
                          onClick={() => handleToggleExpanded(campaign.id)}
                        >
                          {isExpanded ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                      </TableCell>

                      <TableCell>
                        <Typography fontWeight={600}>
                          {campaign.campaignTitle}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        {formatDate(campaign.campaignValidTo)}
                      </TableCell>

                      <TableCell>
                        {getTemplateName(campaign.mailTemplate)}
                      </TableCell>

                      <TableCell>
                        <Chip
                          size='small'
                          label={
                            Number(campaign.active) === 1 ? 'Aktív' : 'Inaktív'
                          }
                          color={
                            Number(campaign.active) === 1
                              ? 'success'
                              : 'default'
                          }
                          variant={
                            Number(campaign.active) === 1
                              ? 'filled'
                              : 'outlined'
                          }
                        />
                      </TableCell>

                      <TableCell align='right'>
                        <IconButton onClick={() => handleEdit(campaign)}>
                          <Edit />
                        </IconButton>

                        <IconButton
                          color='error'
                          onClick={() => handleDelete(campaign)}
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell colSpan={6} sx={{ p: 0, borderBottom: 0 }}>
                        <Collapse in={isExpanded} timeout='auto' unmountOnExit>
                          <Box sx={{ p: 2, bgcolor: 'background.default' }}>
                            <FormMailCampaignDetails campaignId={campaign.id} />
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </>
                );
              })}

              {!loading && filteredCampaigns.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography color='text.secondary'>
                      Nincs megjeleníthető kampány.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}

              {loading && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography color='text.secondary'>Betöltés...</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <CampaignEditorDialog
        open={editorOpen}
        campaign={editedCampaign}
        onClose={() => setEditorOpen(false)}
        onSaved={async () => {
          setEditorOpen(false);
          await loadCampaigns();
        }}
      />
    </Box>
  );
};

export default FormMailCampaign;
